/**
 * Spec 200 Fase 6 #2 — local-storage backed UI preferences.
 *
 * Two preferences live here:
 *
 *   - theme       → 'auto' | 'light' | 'dark'
 *                   'auto' defers to `prefers-color-scheme`. This is
 *                   the default for fresh sessions so a user who
 *                   already has dark-mode-by-default at the OS level
 *                   sees the app dark from the very first paint.
 *
 *   - fontSize    → 'small' | 'medium' | 'large'
 *                   Maps to a body class wrapper that overrides the
 *                   `--typo-*` tokens (Fase 6 #4). Default 'medium'.
 *
 * Both are written by Settings.tsx and read by:
 *   - the Theme provider (Fase 6 #3) — applies `data-theme` on
 *     `<html>` and re-reads on `prefers-color-scheme` changes.
 *   - the Font size wrapper (Fase 6 #4) — applies a class on the
 *     `<html>` element so every nested rule resolves the override.
 *
 * Persistence is best-effort: a Safari private window with cookies
 * disabled throws on `localStorage` access, so reads/writes are
 * wrapped in try/catch and fall back to in-memory defaults.
 */

export const THEME_STORAGE_KEY = 'cuadrilla-web.theme';
export const FONT_SIZE_STORAGE_KEY = 'cuadrilla-web.font-size';

export type ThemePreference = 'auto' | 'light' | 'dark';
export type FontSizePreference = 'small' | 'medium' | 'large';

export const DEFAULT_THEME: ThemePreference = 'auto';
export const DEFAULT_FONT_SIZE: FontSizePreference = 'medium';

const VALID_THEMES: readonly ThemePreference[] = ['auto', 'light', 'dark'];
const VALID_FONT_SIZES: readonly FontSizePreference[] = ['small', 'medium', 'large'];

function safeLocalGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Read the persisted theme preference. Returns the default when no
 * value has been written yet, or the stored value is corrupted (a
 * downgrade from a future schema, manual edit). Validating against a
 * whitelist guarantees the rest of the app never sees an unknown
 * variant.
 */
export function readThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  const raw = safeLocalGet(THEME_STORAGE_KEY);
  if (raw && (VALID_THEMES as readonly string[]).includes(raw)) {
    return raw as ThemePreference;
  }
  return DEFAULT_THEME;
}

export function readFontSizePreference(): FontSizePreference {
  if (typeof window === 'undefined') return DEFAULT_FONT_SIZE;
  const raw = safeLocalGet(FONT_SIZE_STORAGE_KEY);
  if (raw && (VALID_FONT_SIZES as readonly string[]).includes(raw)) {
    return raw as FontSizePreference;
  }
  return DEFAULT_FONT_SIZE;
}

/**
 * Resolve the actual rendering theme — i.e. the value to write into
 * `document.documentElement.dataset.theme`. 'auto' resolves to either
 * 'light' or 'dark' depending on the OS preference at this instant.
 */
export function resolveAppliedTheme(
  preference: ThemePreference,
  matchMedia: typeof window.matchMedia | undefined = typeof window !== 'undefined'
    ? window.matchMedia
    : undefined,
): 'light' | 'dark' {
  if (preference === 'light' || preference === 'dark') return preference;
  if (!matchMedia) return 'light';
  try {
    return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}
